import { AlertCircle } from "lucide-react";
import {
  MediaControlBar,
  MediaController,
  MediaLoadingIndicator,
  MediaTimeDisplay,
  MediaTimeRange,
} from "media-chrome/react";
import { MediaProvider } from "media-chrome/react/media-store";
import { useRef, useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DialogBody, DialogFooter } from "@/components/ui/dialog";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { GenerateThumbnailDto, PublicRecording } from "@classview/shared-types";

import { getRecordingQueryKey } from "../api/get-recording";
import { useModifyThumbnail } from "../api/modify-thumbnail";

type SelectThumbnailFrameProps = {
  videoURL: string;
  recordingId: string;
  handleModalClose: () => void;
};

export function SelectThumbnailFrame({
  videoURL,
  recordingId,
  handleModalClose,
}: SelectThumbnailFrameProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const { organisation } = useOrganisation();

  const {
    mutateAsync: modifyThumbnail,
    isPending: isGenerating,
    isError,
    error,
  } = useModifyThumbnail({
    onSuccess: (thumbnailUrl) => {
      queryClient.setQueryData(
        getRecordingQueryKey(organisation.id, recordingId),
        (old: PublicRecording) => ({
          ...old,
          imageURL: thumbnailUrl,
        }),
      );
      handleModalClose();
    },
  });

  const handleGenerateThumbnail = async () => {
    if (!videoRef.current) return;

    const data: GenerateThumbnailDto = {
      recordingId,
      timestamp: formatTime(currentTime),
    };

    await modifyThumbnail({ data, orgId: organisation.id });
  };

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const time = (e.target as HTMLVideoElement).currentTime;
    setCurrentTime(time);
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <>
      <DialogBody>
        <div className="relative aspect-video w-full cursor-pointer overflow-hidden rounded-lg bg-black">
          <MediaProvider>
            <MediaController
              style={{
                width: "100%",
                aspectRatio: "16/9",
              }}
            >
              <video
                slot="media"
                ref={videoRef}
                playsInline
                preload="auto"
                onTimeUpdate={handleTimeUpdate}
              >
                <source src={videoURL} type="video/mp4" />
              </video>

              <MediaLoadingIndicator
                slot="centered-chrome"
                className="bg-transparent"
              />
              <MediaControlBar
                className="top-auto m-2 rounded-md backdrop-blur-md"
                style={{
                  background:
                    "radial-gradient(85.77% 49.97% at 51% 5.12%, rgba(255, 255, 255, .15) 0, rgba(222, 226, 255, .08) 45.83%, rgba(241, 242, 255, .1) 100%), rgba(0, 0, 0, .2)",
                  boxShadow:
                    "inset 0 1px 0 0 hsla(0,0%,100%,.2), 0px 2px 4px 0px rgba(0, 0, 0, 0.1), 0px 6px 15px 0px rgba(0, 0, 0, 0.15)",
                }}
              >
                <MediaTimeRange className="bg-transparent" />
                <MediaTimeDisplay showDuration className="bg-transparent" />
              </MediaControlBar>
            </MediaController>
          </MediaProvider>
        </div>

        {isError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
      </DialogBody>
      <DialogFooter className="sm:justify-end">
        <Button
          onClick={handleGenerateThumbnail}
          variant="default"
          isLoading={isGenerating}
          disabled={currentTime === 0 || isGenerating}
        >
          {isGenerating ? "Generating..." : "Set Thumbnail"}
        </Button>
      </DialogFooter>
    </>
  );
}
